import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import EActivitiesListSkeleton from '@/components/everridge/activities/EActivitiesListSkeleton';
import EActivitiesElement from '@/components/everridge/activities/EActivitiesElement';
import { DateTimeHelper } from '@/utils/dateTimeHelper';
import { format } from 'date-fns';
import { ETablePaginationWithApi } from '@/components/everridge/table/components/ETablePaginationWithApi';
import {
    TransactionListItemResponseDto,
    TransactionListResponseDto,
} from '@/features/transactions/types/transactions-dto';
import { TransactionsUtils } from '@/features/transactions/utils/transactionsUtils';
import TransactionsInfoListEmpty from '@/features/finances/components/Transactions/TransactionsInfo/TransactionsInfoListEmpty';

type Props = {
    data?: TransactionListResponseDto;
    isLoading?: boolean;
    page: number;
    setPage: (page: number) => void;
    paginationStep?: number;
    showDetails?: (id: string) => void;
};

const TransactionsTableMobile: FC<Props> = ({
    page,
    setPage,
    paginationStep = 10,
    data,
    isLoading,
    showDetails,
}) => {
    const { t } = useTranslation();

    const groupedActivities =
        data?.items?.reduce((acc, curr) => {
            const dateKey = format(new Date(curr.date), 'yyyy-MM-dd');

            if (!acc[dateKey]) {
                acc[dateKey] = [];
            }
            acc[dateKey].push(curr);

            return acc;
        }, {} as Record<string, TransactionListItemResponseDto[]>) || [];

    return (
        <div className="grow flex flex-col gap-4">
            <div className="flex-1">
                {isLoading && <EActivitiesListSkeleton isMobile={true} />}
                {!isLoading && !!data?.items?.length && (
                    <div className="flex flex-col gap-4">
                        {Object.entries(groupedActivities).map(([title, transactions]) => {
                            return (
                                <div key={title}>
                                    <h5 className="text-sm text-popover-800">
                                        {DateTimeHelper.formatDate(new Date(title), 2)}
                                    </h5>
                                    {transactions.map((transaction) => {
                                        const { icon, colorClass } =
                                            TransactionsUtils.getTypeConfig(transaction.type);

                                        const { label: feeLabel } = TransactionsUtils.getFeeConfig(
                                            transaction.sign,
                                        );

                                        const descriptionText = `${
                                            transaction.apartmentName
                                        }\u00A0\u00A0\u00A0·\u00A0\u00A0\u00A0${t(
                                            `${feeLabel ?? ''}`,
                                        )}`;

                                        return (
                                            <EActivitiesElement
                                                key={transaction.id}
                                                icon={icon}
                                                colorClass={colorClass}
                                                header={
                                                    transaction.serviceName ??
                                                    t(
                                                        'main.finances.components.transactions.noServices',
                                                    )
                                                }
                                                description={descriptionText}
                                                value={transaction.amount}
                                                id={transaction.id}
                                                onClick={(id) => showDetails?.(id)}
                                                isMobile={true}
                                                asset={transaction.type}
                                            />
                                        );
                                    })}
                                </div>
                            );
                        })}
                    </div>
                )}
                {!isLoading && !data?.items?.length && <TransactionsInfoListEmpty />}
            </div>
            {!!data?.totalCount && data?.totalCount > 10 && (
                <ETablePaginationWithApi
                    range={0}
                    totalCount={Math.ceil(data?.totalCount / paginationStep)}
                    page={page}
                    setPage={setPage}
					
                />
            )}
        </div>
    );
};

export default TransactionsTableMobile;
